import { useParams, Link, Navigate } from 'react-router-dom'
import { getPostBySlug, getRelatedPosts } from '../data/blogPosts'
import BlogCard from '../components/blog/BlogCard'
import BlogUpvote from '../components/blog/BlogUpvote'
import BlogComments from '../components/blog/BlogComments'
import ScrollReveal from '../components/shared/ScrollReveal'
import { useBlogUpvotes } from '../hooks/useBlogUpvotes'
import './Blog.css'

export default function BlogPost() {
  const { slug } = useParams()
  const post = getPostBySlug(slug)
  const { handleUpvote, hasUpvoted, getUpvoteCount } = useBlogUpvotes()

  // Handle 404
  if (!post) {
    return <Navigate to="/blog" replace />
  }

  const relatedPosts = getRelatedPosts(post)

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <article className="draft-page blog-post-page">
      {/* Header */}
      <header className="blog-post-header">
        <div className="container">
          <ScrollReveal>
            <Link to="/blog" className="back-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6"/>
              </svg>
              Back to Blog
            </Link>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="blog-post-categories">
              {post.categories.map(cat => (
                <span key={cat} className="category-badge">{cat}</span>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <h1 className="blog-post-title">{post.title}</h1>
          </ScrollReveal>

          <ScrollReveal delay={0.3}>
            <div className="blog-post-meta">
              <div className="author-info">
                <span className="author-name">{post.author.name}</span>
                <span className="author-role">{post.author.role}</span>
              </div>
              <div className="post-info">
                <span className="post-date">{formatDate(post.date)}</span>
                <span className="post-read-time">{post.readTime} min read</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage && (
        <ScrollReveal delay={0.4}>
          <div className="blog-post-image">
            <img src={post.featuredImage} alt={post.title} />
          </div>
        </ScrollReveal>
      )}

      {/* Content */}
      <div className="blog-post-content">
        <div className="container-narrow">
          <ScrollReveal delay={0.5}>
            <div
              className="blog-content-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </ScrollReveal>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="blog-post-tags">
              {post.tags.map(tag => (
                <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`} className="tag-pill">
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Upvote Section */}
          <div className="blog-post-actions">
            <BlogUpvote
              count={getUpvoteCount(post.id, post.upvotes)}
              hasUpvoted={hasUpvoted(post.id)}
              onUpvote={() => handleUpvote(post.id)}
            />
          </div>

          {/* Comments Section */}
          <BlogComments postId={post.id} />
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="related-posts-section">
          <div className="container">
            <h2>Related Articles</h2>
            <div className="blog-grid">
              {relatedPosts.map((relPost, i) => (
                <ScrollReveal key={relPost.id} delay={i * 0.1}>
                  <BlogCard
                    post={relPost}
                    upvoteCount={getUpvoteCount(relPost.id, relPost.upvotes)}
                    hasUpvoted={hasUpvoted(relPost.id)}
                    onUpvote={() => handleUpvote(relPost.id)}
                  />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  )
}
